import { DomainId } from '#graphql-operations'
import type { Script } from '@unhead/vue'

const GTM_ZUERICH = 'GTM-K2FR87'

const GTM_ID_MAP: Partial<Record<DomainId, string>> = {
  [DomainId.zuerich_com]: GTM_ZUERICH,
  [DomainId.meeting_zuerich_com]: 'GTM-W6LMKKP',
  [DomainId.zt_zuerich_com]: 'GTM-KFSDJ7J',
  [DomainId.rapperswil_zuerichsee_ch]: 'GTM-TFR6JDR',
  [DomainId.eyz]: 'GTM-KDGCS47K',
  [DomainId.zuerich_experience_com]: 'GTM-NN82SHS3',
}

function getTagManagerID(domain: DomainId): string {
  return GTM_ID_MAP[domain] || GTM_ZUERICH
}

function getGtmSource(domain: DomainId): string {
  if (domain === 'zuerich_com' || domain === 'hoteljobs_zuerich_ch') {
    return 'https://hub.zuerich.com'
  }
  return 'https://www.googletagmanager.com'
}

function getTagManagerScript(domain: DomainId): string {
  const id = getTagManagerID(domain)
  const source = getGtmSource(domain)

  return `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'${source}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');
`
}

function getMetaPixelScript(): string {
  return `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '829332482488417');
    fbq('track', 'PageView');
  `
}

function getScripts(domain: DomainId): Script[] {
  const scripts: Script[] = []

  scripts.push({
    key: 'dataLayerInit',
    innerHTML: `window.dataLayer = [];`,
    tagPriority: -1000,
  })

  scripts.push({
    key: 'usercentrics-consent',
    innerHTML: `
        function gtag() {
          window.dataLayer.push(arguments);
        }

        // By default consent is not given.
        gtag("consent", "default", {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          analytics_storage: "denied",
          wait_for_update: 2000
        });

        gtag("set", "ads_data_redaction", true);
      `,
  })

  if (domain === DomainId.rapperswil_zuerichsee_ch) {
    scripts.push(
      {
        key: 'facebook-pixel',
        type: 'text/javascript',
        innerHTML: getMetaPixelScript(),
      },
      {
        // Rapperswil AI Concierge script
        // @ts-expect-error
        hid: 'concierge-goodguys',
        type: 'text/javascript',
        src: 'https://concierge.goodguys.ai/client/6569e0d7eea26b4665ec8ad0/ai_concierge.js',
        async: true,
      },
    )
  }

  if (!import.meta.dev) {
    scripts.push({
      key: 'gtm-script',
      type: 'text/javascript',
      innerHTML: getTagManagerScript(domain),
    })
  }

  return scripts
}

export default defineNuxtPlugin({
  name: 'zt:analytics',
  dependsOn: ['zt:init-data', 'zt:graphql-middleware'],
  setup() {
    const { activeDomain } = useSiteContext()

    // Kepp track of the previous full path, used to track the referrer.
    const router = useRouter()
    const previousPath = useState('previousPath', () => '')
    router.afterEach((_to, from) => {
      previousPath.value = from.fullPath
    })

    if (import.meta.dev && import.meta.client) {
      try {
        window.dataLayer?.forEach((event) => console.info(event))
      } catch {
        // Noop.
      }
    }

    useHead({
      script: getScripts(activeDomain),
    })
  },
})
